import { useState, type ReactNode } from "react";
import { Microphone, CloudArrowUp, FileText } from "@phosphor-icons/react";

interface Tab {
  id: string;
  label: string;
  icon: typeof Microphone;
}

const TABS: Tab[] = [
  { id: "record", label: "Speak",        icon: Microphone },
  { id: "audio",  label: "Add audio",    icon: CloudArrowUp },
  { id: "text",   label: "Add pages",    icon: FileText },
];

interface StudioTabsProps {
  recordPanel: ReactNode;
  audioPanel: ReactNode;
  textPanel: ReactNode;
  recordingDisabled?: boolean;
}

/**
 * StudioTabs — Archival.
 * Mono labels at +32 tracking. Active tab gets walnut underline +
 * paper-raised tone shift (the lane "comes forward").
 */
export function StudioTabs({ recordPanel, audioPanel, textPanel, recordingDisabled }: StudioTabsProps) {
  const [active, setActive] = useState("record");

  return (
    <div>
      <div className="flex flex-wrap gap-1 border-b border-rule">
        {TABS.map((tab) => {
          const isActive = active === tab.id;
          const disabled = tab.id === "record" && recordingDisabled;
          return (
            <button
              key={tab.id}
              onClick={() => !disabled && setActive(tab.id)}
              disabled={disabled}
              className={
                "flex min-h-11 items-center gap-2 px-3 py-2.5 font-mono text-[10px] tracking-[0.14em] uppercase " +
                "transition-colors duration-as-1 -mb-px " +
                (isActive
                  ? "text-ink-1 border-b-2 border-walnut bg-paper-raised rounded-t-as-2"
                  : disabled
                    ? "text-ink-4 cursor-not-allowed"
                    : "text-ink-3 hover:text-walnut-deep")
              }
            >
              <tab.icon size={14} weight={isActive ? "duotone" : "regular"} />
              {tab.label}
            </button>
          );
        })}
      </div>
      <div className="mt-5">
        {active === "record" && recordPanel}
        {active === "audio" && audioPanel}
        {active === "text" && textPanel}
      </div>
    </div>
  );
}
